import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import CrossIcon from "../../assests/images/dashboard/Vector 3.png";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Renders in header row between title and close (e.g. tab switcher). */
  headerRight?: React.ReactNode;
  width?: string;
  maxWidth?: string;
  minWidth?: string;
  height?: string;
  maxHeight?: string;
  minHeight?: string;
  className?: string;
  /** Optional class for the content wrapper (e.g. reduce padding: py-3) */
  contentClassName?: string;
  showCloseIcon?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  headerRight,
  showCloseIcon = true,
  className = "",
  contentClassName = "",
  width,
  maxWidth,
  minWidth,
  height,
  maxHeight,
  minHeight,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const panelMaxW = width ?? maxWidth;
  const panelStyle: React.CSSProperties = {
    ...(panelMaxW && { maxWidth: panelMaxW, width: "100%" }),
    ...(minWidth && { minWidth }),
    ...(height && { maxHeight: height }),
    ...(maxHeight && { maxHeight }),
    ...(minHeight && { minHeight }),
  };

  const modal = (
    <div aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div
        className="fixed inset-0 z-[100] flex min-h-0 items-center justify-center p-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:p-4"
        role="presentation"
      >
        <button
          type="button"
          tabIndex={-1}
          className="absolute inset-0 bg-black/60 transition-opacity cursor-default border-0 p-0"
          aria-hidden="true"
          onClick={onClose}
        />
        <div
          className={`relative z-10 flex min-h-0 max-h-[min(90dvh,calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-1.5rem))] w-full max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-[18px] bg-white text-left shadow-xl ${className}`}
          style={panelStyle}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white">
            {/* Fixed header: title left, headerRight (e.g. tabs) + close right */}
            <div className="flex shrink-0 items-center gap-3 border-b border-[#F1F1F2] px-4 py-3 sm:px-6 sm:py-4">
              {title && (
                <h1
                  className="min-w-0 flex-1 truncate text-base font-semibold text-[#1A1A1A] sm:text-[18px]"
                  id="modal-title"
                >
                  {title}
                </h1>
              )}
              <div className="flex shrink-0 items-center justify-end gap-3">
                {headerRight}
                {showCloseIcon && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close modal"
                  >
                    <img src={CrossIcon} alt="" className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Content area: flex column so children can fill and control their own scroll (e.g. fixed footer) */}
            <div
              className={`flex min-h-0 flex-1 flex-col overflow-hidden overscroll-contain ${contentClassName || "px-4 py-4 sm:px-8 sm:py-6"}`}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
};

export default Modal;
